import { useCallback, useRef, useState } from "react";
import useDragging from "hooks/useDragging";

import { nonNullable } from "shared/functions";
import { Literals } from "shared/types";
import classNames from "shared/utils/classNames";

type Props = {
  onChange: (files: File[]) => void;
  hint: string;
  allowedExtensions?: string[];
  maxFileSize?: number;
  multiple?: boolean;
  copyTexts: Literals;
};

function Dropper(props: Props) {
  const {
    allowedExtensions,
    hint,
    onChange,
    maxFileSize,
    multiple = true,
    copyTexts,
  } = props;

  const [dropError, setDropError] = useState("");

  const inputRef = useRef<HTMLInputElement>(null);

  const onDropFiles = useCallback(
    (files: File[]) => {
      const tooBigFiles = maxFileSize
        ? files.filter(file => file.size > maxFileSize)
        : files;
      if (tooBigFiles.length) {
        return setDropError(
          tooBigFiles.length > 1
            ? `Files ${tooBigFiles
                .map(file => file.name)
                .join(", ")} are too big`
            : `File ${tooBigFiles
                .map(file => file.name)
                .join(", ")} is too big`,
        );
      }

      const badExtensionFiles = files.filter(file => {
        if (!allowedExtensions) {
          return true;
        }
        const ext = file.name.split(".").at(-1)?.toLowerCase();
        return !allowedExtensions.includes(ext);
      });

      if (badExtensionFiles.length) {
        return setDropError(
          badExtensionFiles.length > 1
            ? `Extensions of the files ${badExtensionFiles
                .map(file => file.name)
                .join(", ")} are not supported`
            : `Extension of the file ${badExtensionFiles
                .map(file => file.name)
                .join(", ")} is not supported`,
        );
      }

      setDropError("");
      onChange(files);
    },
    [allowedExtensions, maxFileSize, onChange],
  );

  const { isDragging } = useDragging(1, onDropFiles, setDropError);

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={
          allowedExtensions
            ? allowedExtensions.map(ext => `.${ext}`).join(",")
            : undefined
        }
        onChange={evt => {
          onDropFiles([...(evt.target?.files || [])].filter(nonNullable));
          evt.target.value = "";
        }}
        multiple={multiple}
        hidden
      />
      <button
        className={classNames(
          "group mx-auto flex h-full w-full flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-grey-800 px-2 py-6 hover:bg-gray-100",
          isDragging ? "bg-gray-100" : "bg-white",
        )}
        onClick={() => inputRef.current?.click()}
      >
        <div className="mx-auto flex w-full max-w-250 flex-col items-center gap-3 text-center">
          <i className="ti ti-cloud-upload flex h-4 w-4 items-center justify-center rounded-full border-[4.8px] border-solid border-grey-200 bg-grey-300 p-1 text-base text-grey-900" />
          <div>
            <span className="font-semibold text-purple-1100">
              {copyTexts.clickToUpload}{" "}
            </span>
            <span className="text-sm">{copyTexts.dragAndDrop}</span>
            <div className="flex gap-1 text-xs">{hint}</div>
            {dropError && <p className="text-xs text-red-700">{dropError}</p>}
          </div>
        </div>
      </button>
    </>
  );
}

export default Dropper;
